import type { SalesChecklistItem } from "@expert/contentful";

import { useEffect } from "react";

import type { EligibleProduct } from "../../../types";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../stores";

const sessionsAlreadyPushed = new Set<string>();

/**
 * RDESQ-1988: When eligibility loads, find Discovery/Engagement sections on the
 * recommended product's sales offer checklist and push each as its own
 * SalesGuidance message into the Expert Assist timeline. Fires at most once per
 * session.
 *
 * NOTE: Reads `isRecommended` directly from the store, so client-side overrides
 * applied at render time (e.g. ATT_IBC PA1 override in `categorizeProductsByType`)
 * are NOT reflected here. Acceptable for now.
 */
export function usePushDiscoveryEngagementToTimeline() {
    const { sessionId, taskData, features, analytics, onSendPromptToTimeline } = useSalesContext();
    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const isFlagOn = features.isUsingMovedDiscoveryAndEngagementQuestions;

    useEffect(() => {
        if (!isFlagOn) return;
        if (!onSendPromptToTimeline) return;
        if (sessionsAlreadyPushed.has(sessionId)) return;
        if (products.length === 0) return;

        const locale = taskData.locale ?? "en-US";
        const recommended = findRecommendedProduct(products, locale);
        if (!recommended) return;

        const productInfo = recommended.productInfo?.[locale] ?? recommended.productInfo?.["en-US"];
        const sections = productInfo?.salesOfferChecklist?.checklistSections ?? [];

        const matching = sections.filter((s) => s.sectionType === "discovery");
        if (matching.length === 0) return;

        sessionsAlreadyPushed.add(sessionId);

        onSendPromptToTimeline({
            type: "SalesMessage",
            title: "Sales Guidance",
            text: "Ask discovery questions while solving to uncover the customer’s devices and how they use them.",
        });

        for (const section of matching) {
            onSendPromptToTimeline({
                type: "SalesGuidance",
                title: section.title,
                text: section.checklistItems.map((i) => i.phrase).join("\n"),
                items: section.checklistItems.map(toSalesGuidanceItem),
            });
            void analytics.dispatcher.dispatchBusinessEvent("SalesGuidanceAutoPushed", {
                productSku: recommended.productSku,
                sectionType: section.sectionType,
                itemCount: section.checklistItems.length,
            });
        }
    }, [isFlagOn, sessionId, products, taskData.locale, analytics, onSendPromptToTimeline]);
}

function findRecommendedProduct(products: EligibleProduct[], locale: string): EligibleProduct | undefined {
    return products.find((p) => (p.productInfo?.[locale] ?? p.productInfo?.["en-US"])?.isRecommended);
}

function toSalesGuidanceItem(item: SalesChecklistItem): {
    phrase: string;
    subheading?: string;
    examples?: string[];
} {
    return {
        phrase: item.phrase,
        subheading: item.subheading,
        examples: item.examples,
    };
}
